import Stack from '@mui/material/Stack';
import type { AnswerWidgets } from '@openx/components-core/lib/AiChat/answerWidgets';
import { MessageRole, type OpenXAgentMessage } from '@openx/components-core/lib/AiChat/types';
import type { ReactNode } from 'react';
import { Alert } from '../Alert/Alert';
import { ChatThinkingContent } from '../ChatThinkingContent/ChatThinkingContent';
import { RichContentActions } from '../RichComponents/RichContentActions';
import { RichContentCard } from '../RichComponents/RichContentCard';
import { getRichContentKey, type RichContentConfig } from '../RichComponents/utils/richContent';
import { ChatMessageAnswer } from './components/ChatMessageAnswer';
import { ChatMessageWrapper } from './components/ChatMessageWrapper';
import { OpenXAgentMarkdown } from './components/OpenXAgentMarkdown';
import { richContentActionsSx, summarySx } from './styles';

export type ChatMessageProps = {
  children?: ReactNode;
  message?: OpenXAgentMessage;
  isUserMessage?: boolean;
  hideTail?: boolean;
  widgets?: AnswerWidgets | undefined;
  richContent?: RichContentConfig;
};

export const ChatMessage = ({
  children,
  message,
  isUserMessage = false,
  hideTail = false,
  widgets,
  richContent: richContentConfig,
}: ChatMessageProps) => {
  const isUser = message ? message.role === MessageRole.USER : isUserMessage;
  const showTail = !hideTail || (message ? message.role !== MessageRole.THINKING : false);

  if (!message) {
    return (
      <ChatMessageWrapper isUser={isUser} showTail={showTail}>
        {children}
      </ChatMessageWrapper>
    );
  }

  if (message.role === MessageRole.ALERT) {
    return <Alert message={message.text} />;
  }

  // Events and widgets carry data only; a widget renders inside the answer that names it.
  if (message.role === MessageRole.EVENT || message.role === MessageRole.WIDGET) {
    return null;
  }

  if (message.role === MessageRole.THINKING) {
    return (
      <ChatMessageWrapper isUser={isUser} showTail={showTail}>
        <ChatThinkingContent thinkingProcessMessages={message.messages} />
      </ChatMessageWrapper>
    );
  }

  if (message.role === MessageRole.AGENT) {
    const richContent = (message.richContent ?? []).map(content => {
      const contentKey = getRichContentKey(message.timestamp, content.id);
      const disabled = richContentConfig?.activeKeys ? !richContentConfig.activeKeys.has(contentKey) : false;

      return { content, contentKey, disabled };
    });
    const hasActions = richContent.some(({ content }) => content.actions?.length);

    return (
      <>
        <ChatMessageWrapper
          isUser={isUser}
          showTail={showTail}
          isFullWidth={widgets !== undefined || richContent.length > 0}
        >
          {message.summary ? (
            <Stack data-test="message-summary" direction="column" sx={summarySx}>
              <OpenXAgentMarkdown content={message.summary} widgets={widgets} />
            </Stack>
          ) : null}
          {/* the answer comes in `text`, as in the Targeting Explainer mock (PR #9240); to be confirmed with the real API */}
          <ChatMessageAnswer content={message.text} widgets={widgets} />
          {richContent.map(({ content, contentKey, disabled }) => (
            <RichContentCard key={contentKey} content={content} disabled={disabled} />
          ))}
        </ChatMessageWrapper>

        {hasActions && (
          <Stack sx={richContentActionsSx}>
            {richContent.map(({ content, contentKey, disabled }) => (
              <RichContentActions
                key={contentKey}
                content={content}
                contentKey={contentKey}
                disabled={disabled}
                confirmedActionKeys={richContentConfig?.confirmedActionKeys}
                onAction={richContentConfig?.onAction}
              />
            ))}
          </Stack>
        )}
      </>
    );
  }

  return (
    <ChatMessageWrapper isUser={isUser} showTail={showTail} isFullWidth={widgets !== undefined}>
      <OpenXAgentMarkdown content={message.text} widgets={widgets} />
    </ChatMessageWrapper>
  );
};
